// The champion moment (AWARDS.md): once, ever, per league season, the first
// time the champion opens a league screen after the final goes FINAL. Team
// colors wash the screen, the cup wears them, confetti and the fanfare play
// (the loudest thing the app ever does), and the one exclamation point in
// system copy gets used.

import { useEffect, useState } from 'react';
import { Button, Colored, Line } from '../../kit/ui';
import { useNavigate } from 'react-router-dom';
import { useLeagueBase, useMyChampionship } from '../../data/hooks';
import { useDelight } from '../../delight/useDelight';
import { DetailSheet } from '../../templates/DetailSheet';
import { Cup, SharePanel } from './AwardKit';
import { TeamFigure, teamFace } from '../../kit/TeamFigure';
import { storageKey } from '../../data/storage';

const SEEN_KEY = storageKey('champion.v1');

function hasSeen(key: string): boolean {
  try {
    return (JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[]).includes(key);
  } catch {
    return false;
  }
}

function markSeen(key: string): void {
  try {
    const keys = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[];
    localStorage.setItem(SEEN_KEY, JSON.stringify([...keys, key].slice(-20)));
  } catch {
    // Storage blocked: the moment may play again next time. Acceptable.
  }
}

export function ChampionMoment() {
  const champ = useMyChampionship();
  const [shown, setShown] = useState(false);
  const [share, setShare] = useState(false);
  const { play, celebrate } = useDelight();
  const navigate = useNavigate();
  const base = useLeagueBase();
  const key = champ?.key ?? null;

  useEffect(() => {
    if (!champ || !key || hasSeen(key)) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    markSeen(key);
    setShown(true);
    play('win');
    celebrate([champ.team.colors.primary, champ.team.colors.secondary, '#ffffff', '#e9b949'], 3000);
    // Once per championship key; the sound and confetti must not replay on re-render.
  }, [key]);

  if (!champ || !shown) return null;
  const { team } = champ;
  const line = champ.finalLine || champ.line;
  return (
    <Colored
      className="fsp-champion"
      role="dialog"
      aria-modal
      aria-label="Champion"
      colors={{ '--p': team.colors.primary, '--s': team.colors.secondary }}
    >
      <div className="fsp-champion-card">
        {/* THE CHAMPION'S CHARACTER CHEERS beside the cup (§4). */}
        <div className="fsp-champion-art">
          <Cup primary={team.colors.primary} secondary={team.colors.secondary} size={132} />
          {teamFace(team) !== null ? <TeamFigure team={team} mood="pleased" height={160} /> : null}
        </div>
        <div className="fsp-champion-title fsp-disp">Champion!</div>
        <div className="fsp-champion-team">{team.name}</div>
        <div className="fsp-champion-sub">
          {champ.trophyName} · {champ.season}
        </div>
        <div className="fsp-champion-line">{line}</div>
        <div className="fsp-champion-actions">
          <Line gap={2} wrap>
            <Button variant="primary" onClick={() => setShare(true)}>
              Share it
            </Button>
            <Button
              onClick={() => {
                setShown(false);
                navigate(`${base}/awards?tab=room`);
              }}
            >
              Trophy room
            </Button>
            <Button variant="quiet" onClick={() => setShown(false)}>
              Close
            </Button>
          </Line>
        </div>
      </div>
      <DetailSheet opened={share} onClose={() => setShare(false)} title="Share it" zIndex={1100}>
        <SharePanel
          input={{
            kicker: `${champ.season} ${champ.trophyName}`,
            headline: team.name,
            subline: team.ownerName,
            line,
            footer: `${champ.leagueName} · ${champ.season}`,
            team,
            tone: 'glory',
          }}
          text={`${team.name} are your ${champ.season} ${champ.trophyName} in ${champ.leagueName}. ${line}`}
        />
      </DetailSheet>
    </Colored>
  );
}
